import Phaser from "phaser";
import caveSource from "./void-cave-source.json";
import caveVoidPng from "../../../@shared/assets/GameMaps/Void-Cave/FDR_Caves_VOID.png";
import cavePalettePng from "../../../@shared/assets/GameMaps/Void-Cave/cave-pallete.png";
import whisperingPropsPng from "../../../@shared/assets/GameMaps/tilesets/forest/TX Props.png";
import AudioManager from "../../../@shared/utils/AudioManager";
import {
  getAvatarSpriteConfig,
  scaleAvatarBodySize,
} from "../../../@shared/utils/SpritesMap";
import { createWalkAnimations } from "../../../@shared/utils/SceneHelpers";
import {
  createLocalOrbAvatarImage,
  getLocalOrbAvatarConfig,
  preloadLocalOrbAvatar,
  updateLocalOrbAvatar,
} from "../../../@shared/utils/phaserOrbAvatar";
import { applyWorldSceneBackground } from "../../../@shared/utils/worldSceneBackgrounds";
import {
  createStoryRuneStoneTeleportEffect,
  destroyStoryRuneStoneTeleportEffect,
  syncStoryRuneStoneTeleportEffect,
} from "../../../@shared/utils/storyRuneStoneTeleportEffect";
import {
  VOID_CAVE_ENTRY_SPAWN,
  VOID_CAVE_LOWER_ENTRANCE_RECT,
  VOID_CAVE_UPPER_DOOR_RECT,
  isVoidCaveBridgePoint,
  isVoidCaveNavigationTileWalkable,
} from "./voidCaveMapConfig";
import { resolveVoidCaveExitRoute } from "./voidCaveExitRoute";
import { VOID_CAVE_DEPTHS_ENTRY_SPAWN } from "./voidCaveDepthsMapConfig";
import {
  createCaveCreatureEncounters,
  destroyCaveCreatureEncounters,
  preloadCaveCreatureEncounters,
  updateCaveCreatureEncounters,
} from "./caveCreatureEncounterController";
import {
  VOID_CAVE_CHEST_ANIMATION_COMPLETE_EVENT,
  attachVoidCaveStoryChestEvents,
  clearVoidCaveStoryChestVisit,
  createVoidCaveChestBodies,
  detachVoidCaveStoryChestEvents,
  drawVoidCaveChestCollisionDebug,
  getOrCreateVoidCaveStoryChestVisit,
  getVoidCaveGroundEntityDepth,
  markVoidCaveStoryChestOpened,
  requestVoidCaveStoryChestOpen,
} from "./voidCaveStoryPackChests";
import {
  CAVE_FOREGROUND_LAYER_NAMES,
  drawDepthSortedCaveForegroundLayer,
} from "./caveDepthSorting";
import caveNpc1Png from "../../../@shared/assets/npcs/spritesheets/FD_Character_003.png";
import caveNpc1IdlePng from "../../../@shared/assets/npcs/spritesheets/FD_Character_003_Idle.png";
import caveNpc2Png from "../../../@shared/assets/npcs/spritesheets/FD_Character_007.png";
import caveNpc2IdlePng from "../../../@shared/assets/npcs/spritesheets/FD_Character_007_Idle.png";
import caveNpc3Png from "../../../@shared/assets/npcs/spritesheets/FD_Character_016.png";
import caveNpc3IdlePng from "../../../@shared/assets/npcs/spritesheets/FD_Character_016_Idle.png";
import caveNpc4Png from "../../../@shared/assets/npcs/spritesheets/FD_Character_001.png";
import caveNpc4IdlePng from "../../../@shared/assets/npcs/spritesheets/FD_Character_001_Idle.png";

const DOOR_SFX = require("../../../@shared/assets/sounds/door.mp3");
const TILE_SIZE = 32;
const PLAYER_DEPTH = 900;
const BRIDGE_PLAYER_DEPTH = 1300;
const EXIT_PORTAL = VOID_CAVE_LOWER_ENTRANCE_RECT;
const UPPER_DOOR = VOID_CAVE_UPPER_DOOR_RECT;
const UPPER_DOOR_LABEL = "Enter The Deep Hollows";
const COLLISION_LAYER_NAMES = ["CaveWall", "Holes", "Rocks"];
const NPC_FRAME_SIZE = 24;
const NPC_SCALE = 2.2;
const NPC_MOVE_SPEED = 0.38;
const NPC_WAIT_MS = 1800;
const NPC_CHAT_BUBBLE_MS = 2600;
const CAVE_AMBIENT_PARTICLE_DEPTH = 1180;
const STORY_RUNE_STONE_TELEPORT_Y_OFFSET = 18;
const CHEST_TEXTURE_RECTS = {
  closed: { x: 96, y: 30, width: 32, height: 31 },
  open: { x: 96, y: 76, width: 32, height: 49 },
};
const CAVE_NPC_PATROLS = [
  {
    key: "vcaveNpcScout",
    texture: "vcaveNpcScout",
    idleTexture: "vcaveNpcScoutIdle",
    promptLabel: "Talk To Scout",
    speech: "The cave shifts, but the safe paths remember your steps.",
    start: { x: 216, y: 1606 },
    path: [{ x: 216, y: 1606 }, { x: 312, y: 1606 }],
  },
  {
    key: "vcaveNpcWatcher",
    texture: "vcaveNpcWatcher",
    idleTexture: "vcaveNpcWatcherIdle",
    promptLabel: "Talk To Watcher",
    speech: "Some rocks sparkle because they hide more than minerals.",
    start: { x: 604, y: 1268 },
    path: [{ x: 604, y: 1268 }, { x: 764, y: 1268 }, { x: 764, y: 1320 }, { x: 604, y: 1320 }],
  },
  {
    key: "vcaveNpcGuide",
    texture: "vcaveNpcGuide",
    idleTexture: "vcaveNpcGuideIdle",
    promptLabel: "Talk To Guide",
    speech: "If the air gets colder, you're getting closer to the deeper hollows.",
    start: { x: 858, y: 1002 },
    path: [{ x: 858, y: 1002 }, { x: 938, y: 1002 }],
  },
  {
    key: "vcaveNpcMiner",
    texture: "vcaveNpcMiner",
    idleTexture: "vcaveNpcMinerIdle",
    promptLabel: "Talk To Miner",
    speech: "Treasure rarely sits on the main path. Caves like a little curiosity.",
    start: { x: 1144, y: 1364 },
    path: [{ x: 1144, y: 1364 }, { x: 1210, y: 1364 }],
  },
];

const MAP_WIDTH = caveSource.width * caveSource.tilewidth;
const MAP_HEIGHT = caveSource.height * caveSource.tileheight;

export default class VoidCaveScene extends Phaser.Scene {
  constructor() {
    super({ key: "VoidCaveScene" });
  }

  init(data = {}) {
    const exitRoute = resolveVoidCaveExitRoute(data);
    this.spawnPoint = data.spawn || VOID_CAVE_ENTRY_SPAWN;
    this.returnSpawn = exitRoute.spawn;
    this.cityReturnSpawn = exitRoute.cityReturnSpawn;
    this.returnSceneKey = exitRoute.sceneKey;
    this.returnDoorLabel = exitRoute.doorLabel;
    this.heroUrl = data.heroUrl || null;
    this.heroAvatarKey = data.heroAvatarKey || "io_default";
    this.heroSpriteConfig = getAvatarSpriteConfig(this.heroAvatarKey);
    this.userData = data.userData || null;
    const { avatarData, textureKey } = getLocalOrbAvatarConfig(this.userData);
    this.localOrbAvatarData = avatarData;
    this.localOrbTextureKey = textureKey;
  }

  preload() {
    if (this.heroUrl && !this.textures.exists("hero")) {
      this.load.spritesheet("hero", this.heroUrl, {
        frameWidth: this.heroSpriteConfig.frameWidth,
        frameHeight: this.heroSpriteConfig.frameHeight,
      });
    }
    preloadLocalOrbAvatar(this, this.localOrbAvatarData, this.localOrbTextureKey);
    preloadCaveCreatureEncounters(this);

    this.load.spritesheet("vcaveBase", caveVoidPng, {
      frameWidth: TILE_SIZE,
      frameHeight: TILE_SIZE,
    });
    this.load.spritesheet("vcavePalette", cavePalettePng, {
      frameWidth: TILE_SIZE,
      frameHeight: TILE_SIZE,
    });
    this.load.image("vcavePropsAtlas", whisperingPropsPng);
    this.load.spritesheet("vcaveNpcScout", caveNpc1Png, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("vcaveNpcScoutIdle", caveNpc1IdlePng, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("vcaveNpcWatcher", caveNpc2Png, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("vcaveNpcWatcherIdle", caveNpc2IdlePng, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("vcaveNpcGuide", caveNpc3Png, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("vcaveNpcGuideIdle", caveNpc3IdlePng, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("vcaveNpcMiner", caveNpc4Png, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });
    this.load.spritesheet("vcaveNpcMinerIdle", caveNpc4IdlePng, {
      frameWidth: NPC_FRAME_SIZE,
      frameHeight: NPC_FRAME_SIZE,
    });

    AudioManager.loadSFX("door", DOOR_SFX);
  }

  create() {
    applyWorldSceneBackground(this);
    this.mapSource = caveSource;
    this.buildTilesetRegistry();
    this.ensureAtlasTextures();
    this.ensureAmbientParticleTextures();
    this.ensureNpcAnimations();
    this.chestSprites = new Map();
    this.chestBodies = new Map();
    this.patrolNpcs = [];
    this.currentNpc = null;
    this.currentInteractable = null;
    this.chatBubble = null;
    this.chatBubbleText = null;
    this.chatBubbleTarget = null;
    this.chatBubbleHideTimer = null;
    this.storyRuneStoneTeleportEffect = null;
    this.storyRandomChestSpecs = getOrCreateVoidCaveStoryChestVisit({
      source: caveSource,
      sceneKey: this.scene.key,
      entrySpawn: this.spawnPoint,
      reservedPoints: CAVE_NPC_PATROLS.map((patrol) => patrol.start),
    });
    this.drawMapFromSource();
    this.createCollisionBodiesFromLayers();
    this.storyRandomChestSpecs.forEach((chest) => this.createChest(chest));
    attachVoidCaveStoryChestEvents(this);
    this.createPatrolNpcs();
    this.createAmbientParticles();

    this.matter.world.setBounds(0, 0, MAP_WIDTH, MAP_HEIGHT);
    this.cameras.main.setBounds(0, 0, MAP_WIDTH, MAP_HEIGHT);

    this.player = this.matter.add
      .sprite(this.spawnPoint.x, this.spawnPoint.y, "hero", 0)
      .setBody(
        scaleAvatarBodySize(this.heroAvatarKey, { width: 64, height: 80 }),
      )
      .setScale(this.heroSpriteConfig.worldScale || 1)
      .setFixedRotation()
      .setDepth(this.getPlayerDepthAt(this.spawnPoint.x, this.spawnPoint.y));
    this.playerOrbImage = createLocalOrbAvatarImage(
      this,
      this.player,
      this.spawnPoint,
      this.localOrbAvatarData,
      this.localOrbTextureKey,
      { depth: this.player.depth },
    );
    createCaveCreatureEncounters(this, {
      source: caveSource,
      storyMapId: "voidCave",
      encounterCount: 5,
      reservedPoints: [
        ...this.storyRandomChestSpecs,
        ...CAVE_NPC_PATROLS.map((patrol) => patrol.start),
      ],
    });

    createWalkAnimations(this, "hero", this.heroAvatarKey);

    const isMobile =
      this.sys.game.device.os.android || this.sys.game.device.os.iOS;
    this.cameras.main
      .startFollow(this.player, true, 0.1, 0.1)
      .setZoom(isMobile ? 1.1 : 1.65);

    this.createExitPortal();
    this.createUpperDoor();

    this.currentDoor = null;
    this.joystick = { x: 0, y: 0 };
    this.cursors = this.input.keyboard.createCursorKeys();

    if (this.userData?.activeGameMode === "story") {
      window.dispatchEvent(
        new CustomEvent("story-world-location", {
          detail: {
            sceneKey: this.scene.key,
            world: {
              mapId: "voidCave",
              building: "",
              spawnId: "void-cave-entry",
              x: this.spawnPoint.x,
              y: this.spawnPoint.y,
            },
          },
        }),
      );
    }
    this.keys = this.input.keyboard.addKeys({
      W: Phaser.Input.Keyboard.KeyCodes.W,
      A: Phaser.Input.Keyboard.KeyCodes.A,
      S: Phaser.Input.Keyboard.KeyCodes.S,
      D: Phaser.Input.Keyboard.KeyCodes.D,
    });
    this.spaceKey = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.SPACE,
    );

    this.matter.world.on("collisionstart", (event) => {
      event.pairs.forEach(({ bodyA, bodyB }) => {
        const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
        const isPlayer =
          bodyA === this.player.body || bodyB === this.player.body;
        if (!sensor || !isPlayer) return;

        if (sensor.label === "exit") {
          this.currentDoor = "exit";
          this.currentInteractable = { type: "exit" };
          this.setInteractPrompt(this.returnDoorLabel);
        } else if (sensor.label === "upperDoor") {
          this.currentDoor = "upperDoor";
          this.currentInteractable = { type: "upperDoor" };
          this.setInteractPrompt(UPPER_DOOR_LABEL);
        } else if (sensor.label?.startsWith("chest:")) {
          const chestId = sensor.label.slice(6);
          const chest = this.chestSprites.get(chestId);
          if (!chest || chest.opened) return;
          this.currentInteractable = { type: "chest", id: chestId };
          this.setInteractPrompt("Open Chest");
        } else if (sensor.label?.startsWith("npc:")) {
          const npc = this.patrolNpcs.find((entry) => entry.id === sensor.label.slice(4));
          if (npc) this.setCaveNpcIdle(npc);
        }
      });
    });

    this.matter.world.on("collisionend", (event) => {
      event.pairs.forEach(({ bodyA, bodyB }) => {
        const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
        const isPlayer =
          bodyA === this.player.body || bodyB === this.player.body;
        if (!sensor || !isPlayer) return;

        if (sensor.label === "exit") {
          this.currentDoor = null;
          if (this.currentInteractable?.type === "exit") {
            this.currentInteractable = null;
            this.clearInteractPrompt();
          }
        } else if (sensor.label === "upperDoor") {
          if (this.currentDoor === "upperDoor") this.currentDoor = null;
          if (this.currentInteractable?.type === "upperDoor") {
            this.currentInteractable = null;
            this.clearInteractPrompt();
          }
        } else if (
          sensor.label?.startsWith("chest:")
          && this.currentInteractable?.type === "chest"
          && this.currentInteractable.id === sensor.label.slice(6)
        ) {
          this.currentInteractable = null;
          this.clearInteractPrompt();
        }
      });
    });

    this._onEnter = () => {
      if (this.currentInteractable?.type === "chest") {
        this.requestOpenChest(this.currentInteractable.id);
        return;
      }
      if (this.currentNpc && !this.currentInteractable && !this.currentDoor) {
        this.showNpcChatBubble(this.currentNpc);
        return;
      }
      if (this.currentDoor === "upperDoor") {
        AudioManager.playSFX("door");
        window.dispatchEvent(
          new CustomEvent("interact-proximity", { detail: false }),
        );
        window.dispatchEvent(new CustomEvent("door-name", { detail: null }));
        this.currentDoor = null;
        this.cameras.main.fadeOut(300, 0, 0, 0);
        this.cameras.main.once("camerafadeoutcomplete", () => {
          clearVoidCaveStoryChestVisit(this.scene.key);
          this.scene.start("VoidCaveDepthsScene", {
            spawn: VOID_CAVE_DEPTHS_ENTRY_SPAWN,
            cityReturnSpawn: this.cityReturnSpawn,
            heroUrl: this.heroUrl,
            heroAvatarKey: this.heroAvatarKey,
            userData: this.userData,
          });
        });
        return;
      }
      if (this.currentDoor !== "exit") return;
      AudioManager.playSFX("door");
      window.dispatchEvent(
        new CustomEvent("interact-proximity", { detail: false }),
      );
      window.dispatchEvent(new CustomEvent("door-name", { detail: null }));
      this.currentDoor = null;
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.cameras.main.once("camerafadeoutcomplete", () => {
        clearVoidCaveStoryChestVisit(this.scene.key);
        this.scene.start(this.returnSceneKey, {
          spawn: this.returnSpawn,
          returnSpawn: this.returnSpawn,
          cityReturnSpawn: this.cityReturnSpawn,
          heroUrl: this.heroUrl,
          heroAvatarKey: this.heroAvatarKey,
          userData: this.userData,
        });
      });
    };

    this.spaceKey.on("down", this._onEnter);
    window.addEventListener("press-enter", this._onEnter);
    this._onStoryRuneStoneTeleport = (event) => {
      const targetSceneKey = String(event?.detail?.sceneKey || "");
      if (targetSceneKey && targetSceneKey !== this.scene.key) return;
      if (this.userData?.activeGameMode !== "story") return;
      this.playStoryRuneStoneTeleportEffect(
        String(event?.detail?.effectId || ""),
      );
    };
    window.addEventListener(
      "story-rune-stone-teleport",
      this._onStoryRuneStoneTeleport,
    );
    this.events.once("shutdown", () => {
      this.spaceKey?.off?.("down", this._onEnter);
      window.removeEventListener("press-enter", this._onEnter);
      window.removeEventListener(
        "story-rune-stone-teleport",
        this._onStoryRuneStoneTeleport,
      );
      this.destroyStoryRuneStoneTeleportEffect();
      detachVoidCaveStoryChestEvents(this);
      destroyCaveCreatureEncounters(this);
      this.chatBubbleHideTimer?.remove(false);
      window.dispatchEvent(
        new CustomEvent("interact-proximity", { detail: false }),
      );
      window.dispatchEvent(new CustomEvent("door-name", { detail: null }));
    });

    this.cameras.main.fadeIn(300, 0, 0, 0);
  }

  emitStoryRuneStoneTeleportComplete(effectId = "") {
    if (typeof window === "undefined" || !effectId) return;

    window.dispatchEvent(
      new CustomEvent("story-rune-stone-teleport-complete", {
        detail: {
          effectId,
          sceneKey: this.scene.key,
        },
      }),
    );
  }

  getStoryRuneStoneTeleportAnchor() {
    const position = this.player?.body?.position;
    if (
      position &&
      Number.isFinite(position.x) &&
      Number.isFinite(position.y)
    ) {
      return {
        x: position.x,
        y: position.y,
      };
    }

    return null;
  }

  playStoryRuneStoneTeleportEffect(effectId = "") {
    if (!effectId) return;

    this.destroyStoryRuneStoneTeleportEffect();

    const anchor = this.getStoryRuneStoneTeleportAnchor();
    if (!anchor) {
      this.emitStoryRuneStoneTeleportComplete(effectId);
      return;
    }

    this.storyRuneStoneTeleportEffect = createStoryRuneStoneTeleportEffect(this, {
      x: anchor.x,
      y: anchor.y + STORY_RUNE_STONE_TELEPORT_Y_OFFSET,
      depth: (this.player?.depth || PLAYER_DEPTH) + 120,
      target: this.player,
      onComplete: () => {
        this.storyRuneStoneTeleportEffect = null;
        this.emitStoryRuneStoneTeleportComplete(effectId);
      },
    });
  }

  syncStoryRuneStoneTeleportEffect() {
    const anchor = this.getStoryRuneStoneTeleportAnchor();
    if (!anchor || !this.storyRuneStoneTeleportEffect) return;

    syncStoryRuneStoneTeleportEffect(this.storyRuneStoneTeleportEffect, {
      x: anchor.x,
      y: anchor.y + STORY_RUNE_STONE_TELEPORT_Y_OFFSET,
      depth: (this.player?.depth || PLAYER_DEPTH) + 120,
    });
  }

  destroyStoryRuneStoneTeleportEffect() {
    destroyStoryRuneStoneTeleportEffect(this.storyRuneStoneTeleportEffect);
    this.storyRuneStoneTeleportEffect = null;
  }

  buildTilesetRegistry() {
    const textureKeyByBasename = {
      "FDR_Caves_VOID.tsx": "vcaveBase",
      "cave-pallete.tsx": "vcavePalette",
    };

    this.tilesetRegistry = [...(this.mapSource?.tilesets || [])]
      .map((tileset) => ({
        ...tileset,
        textureKey:
          textureKeyByBasename[
            tileset.basename || tileset.source?.split("/").pop()
          ],
      }))
      .filter((tileset) => tileset.textureKey)
      .sort((a, b) => a.firstgid - b.firstgid);
  }

  resolveTilesetForGid(gid) {
    if (!gid) return null;

    let found = null;
    this.tilesetRegistry.forEach((tileset) => {
      if (tileset.firstgid <= gid) found = tileset;
    });

    if (!found) return null;
    return {
      ...found,
      frame: gid - found.firstgid,
    };
  }

  getLayerByName(name) {
    return this.mapSource?.layers?.find(
      (layer) => layer.type === "tilelayer" && layer.name === name,
    );
  }

  drawStaticLayerFromSource(layerName, depth) {
    const layer = this.getLayerByName(layerName);
    if (!layer) return;

    const rt = this.make.renderTexture({
      width: MAP_WIDTH,
      height: MAP_HEIGHT,
      add: false,
    });

    layer.data.forEach((gid, index) => {
      if (!gid) return;
      const resolved = this.resolveTilesetForGid(gid);
      if (!resolved) return;

      const tx = index % layer.width;
      const ty = Math.floor(index / layer.width);
      rt.drawFrame(
        resolved.textureKey,
        resolved.frame,
        tx * TILE_SIZE,
        ty * TILE_SIZE,
      );
    });

    const textureKey = `vcave-${layerName}`;
    if (this.textures.exists(textureKey)) {
      this.textures.remove(textureKey);
    }
    rt.saveTexture(textureKey);
    this.add.image(0, 0, textureKey).setOrigin(0).setDepth(depth);
  }

  drawMapFromSource() {
    this.drawStaticLayerFromSource("CaveGround", 1);
    this.drawStaticLayerFromSource("Ground", 2);
    this.drawStaticLayerFromSource("Holes", 3);
    CAVE_FOREGROUND_LAYER_NAMES.forEach((layerName) => {
      drawDepthSortedCaveForegroundLayer(this, {
        layerName,
        tileSize: TILE_SIZE,
      });
    });
  }

  createCollisionBodiesFromLayers() {
    COLLISION_LAYER_NAMES.forEach((name) => {
      const layer = this.getLayerByName(name);
      if (!layer) return;

      for (let ty = 0; ty < layer.height; ty += 1) {
        let runStart = null;
        for (let tx = 0; tx <= layer.width; tx += 1) {
          const gid = tx < layer.width ? layer.data[ty * layer.width + tx] : 0;
          const solid =
            Boolean(gid) && !isVoidCaveNavigationTileWalkable(tx, ty);

          if (solid && runStart === null) {
            runStart = tx;
          } else if (!solid && runStart !== null) {
            const runWidth = tx - runStart;
            this.matter.add.rectangle(
              runStart * TILE_SIZE + (runWidth * TILE_SIZE) / 2,
              ty * TILE_SIZE + TILE_SIZE / 2,
              runWidth * TILE_SIZE,
              TILE_SIZE,
              {
                isStatic: true,
                label: "terrain",
              },
            );
            runStart = null;
          }
        }
      }
    });
  }

  createExitPortal() {
    const centerX = EXIT_PORTAL.x + EXIT_PORTAL.width / 2;
    const centerY = EXIT_PORTAL.y + EXIT_PORTAL.height / 2;

    this.matter.add.rectangle(
      centerX,
      centerY,
      EXIT_PORTAL.width,
      EXIT_PORTAL.height,
      {
        isStatic: true,
        isSensor: true,
        label: "exit",
      },
    );

    const glow = this.add.graphics().setDepth(3000);
    glow.fillStyle(0x8d72ff, 0.16);
    glow.fillCircle(centerX, centerY, 46);
    glow.fillStyle(0xb9a6ff, 0.28);
    glow.fillCircle(centerX, centerY, 28);
    glow.lineStyle(3, 0xf2efff, 0.95);
    glow.strokeCircle(centerX, centerY, 18);

    this.add
      .text(centerX, centerY - 38, "Return", {
        font: "16px Arial",
        fill: "#f7f3ff",
        stroke: "#352554",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(3001);
  }

  createUpperDoor() {
    const centerX = UPPER_DOOR.x + UPPER_DOOR.width / 2;
    const centerY = UPPER_DOOR.y + UPPER_DOOR.height / 2;

    this.matter.add.rectangle(
      centerX,
      centerY,
      UPPER_DOOR.width,
      UPPER_DOOR.height,
      {
        isStatic: true,
        isSensor: true,
        label: "upperDoor",
      },
    );

    const glow = this.add.graphics().setDepth(3000);
    glow.fillStyle(0x8d72ff, 0.14);
    glow.fillEllipse(centerX, centerY - 20, 150, 56);
    glow.fillStyle(0xb9a6ff, 0.22);
    glow.fillCircle(centerX, centerY - 24, 22);

    this.add
      .text(centerX, centerY - 62, "Deep Hollows", {
        font: "16px Arial",
        fill: "#f7f3ff",
        stroke: "#352554",
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(3001);
  }

  ensureAtlasTextures() {
    const propSource = this.textures.get("vcavePropsAtlas")?.getSourceImage();
    if (!propSource) return;

    Object.entries(CHEST_TEXTURE_RECTS).forEach(([kind, rect]) => {
      const key = `vcave-chest-${kind}`;
      if (this.textures.exists(key)) return;

      const canvasTexture = this.textures.createCanvas(
        key,
        rect.width,
        rect.height,
      );
      const ctx = canvasTexture.context;
      ctx.drawImage(
        propSource,
        rect.x,
        rect.y,
        rect.width,
        rect.height,
        0,
        0,
        rect.width,
        rect.height,
      );
      canvasTexture.refresh();
    });
  }

  ensureAmbientParticleTextures() {
    if (!this.textures.exists("vcaveAmbientGlow")) {
      const glowTexture = this.textures.createCanvas("vcaveAmbientGlow", 12, 12);
      const glowCtx = glowTexture.context;
      const gradient = glowCtx.createRadialGradient(6, 6, 1, 6, 6, 6);
      gradient.addColorStop(0, "rgba(210, 232, 255, 0.92)");
      gradient.addColorStop(0.45, "rgba(152, 171, 255, 0.55)");
      gradient.addColorStop(1, "rgba(152, 171, 255, 0)");
      glowCtx.fillStyle = gradient;
      glowCtx.fillRect(0, 0, 12, 12);
      glowTexture.refresh();
    }

    if (!this.textures.exists("vcaveAmbientDust")) {
      const dustTexture = this.textures.createCanvas("vcaveAmbientDust", 12, 8);
      const dustCtx = dustTexture.context;
      dustCtx.fillStyle = "rgba(178, 170, 148, 0.95)";
      dustCtx.beginPath();
      dustCtx.ellipse(6, 4, 4.6, 2.4, -0.2, 0, Math.PI * 2);
      dustCtx.fill();
      dustCtx.strokeStyle = "rgba(120, 110, 88, 0.7)";
      dustCtx.lineWidth = 1;
      dustCtx.beginPath();
      dustCtx.moveTo(3.2, 5.1);
      dustCtx.lineTo(8.5, 2.9);
      dustCtx.stroke();
      dustTexture.refresh();
    }

    if (!this.textures.exists("vcaveAmbientMist")) {
      const mistTexture = this.textures.createCanvas("vcaveAmbientMist", 18, 18);
      const mistCtx = mistTexture.context;
      const mistGradient = mistCtx.createRadialGradient(9, 9, 2, 9, 9, 9);
      mistGradient.addColorStop(0, "rgba(230, 236, 255, 0.66)");
      mistGradient.addColorStop(0.6, "rgba(180, 188, 228, 0.28)");
      mistGradient.addColorStop(1, "rgba(180, 188, 228, 0)");
      mistCtx.fillStyle = mistGradient;
      mistCtx.beginPath();
      mistCtx.arc(9, 9, 9, 0, Math.PI * 2);
      mistCtx.fill();
      mistTexture.refresh();
    }
  }

  ensureNpcAnimations() {
    CAVE_NPC_PATROLS.forEach((npc) => {
      ["down", "left", "right", "up"].forEach((dir, index) => {
        const walkKey = `${npc.key}-walk-${dir}`;
        if (!this.anims.exists(walkKey)) {
          this.anims.create({
            key: walkKey,
            frames: this.anims.generateFrameNumbers(npc.texture, {
              start: 4 * index,
              end: 4 * index + 3,
            }),
            frameRate: 6,
            repeat: -1,
          });
        }

        const idleKey = `${npc.key}-idle-${dir}`;
        if (!this.anims.exists(idleKey)) {
          this.anims.create({
            key: idleKey,
            frames: this.anims.generateFrameNumbers(npc.idleTexture, {
              start: 4 * index,
              end: 4 * index + 3,
            }),
            frameRate: 4,
            repeat: -1,
          });
        }
      });
    });
  }

  createAmbientParticles() {
    const glowParticles = this.add.particles(0, 0, "vcaveAmbientGlow", {
      x: { min: 144, max: 992 },
      y: { min: 880, max: 1712 },
      lifespan: { min: 3000, max: 4600 },
      speedX: { min: -9, max: 9 },
      speedY: { min: -20, max: -8 },
      scale: { start: 0.7, end: 0.14 },
      alpha: { start: 0.34, end: 0 },
      blendMode: "ADD",
      frequency: 440,
      quantity: 2,
    });
    glowParticles.setDepth(CAVE_AMBIENT_PARTICLE_DEPTH);

    const dustParticles = this.add.particles(0, 0, "vcaveAmbientDust", {
      x: { min: 160, max: 1180 },
      y: { min: 860, max: 1820 },
      lifespan: { min: 4600, max: 6800 },
      speedX: { min: -18, max: 14 },
      speedY: { min: 7, max: 15 },
      rotate: { min: -22, max: 22 },
      angularVelocity: { min: -12, max: 12 },
      scale: { start: 0.66, end: 0.38 },
      alpha: { start: 0.34, end: 0 },
      frequency: 720,
      quantity: 2,
    });
    dustParticles.setDepth(CAVE_AMBIENT_PARTICLE_DEPTH - 20);

    const mistPoints = [
      { x: 286, y: 1608 },
      { x: 654, y: 1288 },
      { x: 910, y: 1020 },
      { x: 1146, y: 1390 },
    ];

    mistPoints.forEach((point) => {
      const mistParticles = this.add.particles(point.x, point.y, "vcaveAmbientMist", {
        lifespan: { min: 1800, max: 2600 },
        speedX: { min: -4, max: 4 },
        speedY: { min: -22, max: -12 },
        scale: { start: 0.52, end: 1.08 },
        alpha: { start: 0.22, end: 0 },
        rotate: { min: -8, max: 8 },
        frequency: 1150,
        quantity: 1,
      });
      mistParticles.setDepth(CAVE_AMBIENT_PARTICLE_DEPTH - 10);
    });
  }

  createPatrolNpcs() {
    CAVE_NPC_PATROLS.forEach((config) => {
      const sprite = this.add
        .sprite(config.start.x, config.start.y, config.idleTexture, 0)
        .setOrigin(0.5, 1)
        .setScale(NPC_SCALE)
        .setDepth(this.getGroundEntityDepthAt(config.start.y + 2));

      const body = this.matter.add.rectangle(
        config.start.x,
        config.start.y - 12,
        28,
        26,
        {
          isStatic: true,
          isSensor: true,
          label: `npc:${config.key}`,
        },
      );

      this.patrolNpcs.push({
        ...config,
        id: config.key,
        sprite,
        body,
        pathIndex: 1 % config.path.length,
        waitUntil: this.time.now + Phaser.Math.Between(200, 800),
        lastDirection: "down",
      });

      this.setCaveNpcIdle(this.patrolNpcs[this.patrolNpcs.length - 1], "down");
    });
  }

  setCaveNpcIdle(npc, direction = npc.lastDirection || "down") {
    npc.lastDirection = direction;
    const animKey = `${npc.key}-idle-${direction}`;
    if (npc.sprite.anims.currentAnim?.key !== animKey) {
      npc.sprite.play(animKey, true);
    }
  }

  showNpcChatBubble(npc) {
    if (!npc) return;

    npc.waitUntil = Math.max(npc.waitUntil || 0, this.time.now + NPC_CHAT_BUBBLE_MS);
    npc.talkingUntil = this.time.now + NPC_CHAT_BUBBLE_MS;
    this.setCaveNpcIdle(npc);

    if (!this.chatBubble) {
      this.chatBubble = this.add.container(0, 0).setDepth(4100);
      const bubbleBg = this.add.graphics();
      const pointer = this.add.graphics();
      const text = this.add.text(0, 0, "", {
        font: "16px Arial",
        fill: "#1d1b32",
        align: "center",
        wordWrap: { width: 190, useAdvancedWrap: true },
      });
      text.setOrigin(0.5, 0.5);
      this.chatBubble.add([bubbleBg, pointer, text]);
      this.chatBubble.bg = bubbleBg;
      this.chatBubble.pointer = pointer;
      this.chatBubbleText = text;
      this.chatBubble.setVisible(false);
    }

    this.chatBubbleTarget = npc;
    this.chatBubbleText.setText(npc.speech || "The cave is quieter when you listen.");
    const paddingX = 16;
    const paddingY = 12;
    const width = this.chatBubbleText.width + paddingX * 2;
    const height = this.chatBubbleText.height + paddingY * 2;

    this.chatBubble.bg.clear();
    this.chatBubble.bg.fillStyle(0xf8f2ff, 0.96);
    this.chatBubble.bg.lineStyle(3, 0x40305c, 1);
    this.chatBubble.bg.fillRoundedRect(-width / 2, -height / 2, width, height, 12);
    this.chatBubble.bg.strokeRoundedRect(-width / 2, -height / 2, width, height, 12);

    this.chatBubble.pointer.clear();
    this.chatBubble.pointer.fillStyle(0xf8f2ff, 0.96);
    this.chatBubble.pointer.lineStyle(3, 0x40305c, 1);
    this.chatBubble.pointer.beginPath();
    this.chatBubble.pointer.moveTo(-10, height / 2 - 2);
    this.chatBubble.pointer.lineTo(0, height / 2 + 14);
    this.chatBubble.pointer.lineTo(10, height / 2 - 2);
    this.chatBubble.pointer.closePath();
    this.chatBubble.pointer.fillPath();
    this.chatBubble.pointer.strokePath();

    this.chatBubble.setVisible(true);
    this.updateNpcChatBubblePosition();

    this.chatBubbleHideTimer?.remove(false);
    this.chatBubbleHideTimer = this.time.delayedCall(NPC_CHAT_BUBBLE_MS, () => {
      this.hideNpcChatBubble(npc);
    });
  }

  hideNpcChatBubble(npc = null) {
    if (npc && this.chatBubbleTarget !== npc) return;
    this.chatBubbleTarget = null;
    this.chatBubbleHideTimer = null;
    this.chatBubble?.setVisible(false);
  }

  updateNpcChatBubblePosition() {
    if (!this.chatBubble?.visible || !this.chatBubbleTarget?.sprite) return;
    this.chatBubble.setPosition(
      this.chatBubbleTarget.sprite.x,
      this.chatBubbleTarget.sprite.y - 78,
    );
  }

  setNpcInteractionTarget(npc) {
    if (this.currentInteractable || this.currentDoor) {
      this.currentNpc = npc;
      return;
    }
    if (this.currentNpc === npc) return;

    this.currentNpc = npc;
    if (npc) {
      this.setInteractPrompt(npc.promptLabel || "Talk");
      return;
    }

    this.clearInteractPrompt();
  }

  createChest(chest) {
    const rect = CHEST_TEXTURE_RECTS.closed;
    const isOpened = Boolean(chest.opened);
    const spriteY = isOpened
      ? chest.y - (CHEST_TEXTURE_RECTS.open.height - rect.height)
      : chest.y;
    const sprite = this.add
        .image(
          chest.x,
          spriteY,
          isOpened ? "vcave-chest-open" : "vcave-chest-closed",
        )
        .setOrigin(0, 0)
        .setDepth(this.getGroundEntityDepthAt(chest.y + rect.height - 4));

    const { collisionBody, interactionBody } = createVoidCaveChestBodies(
      this,
      chest,
      rect,
      { isOpened },
    );
    const { collisionDebugGraphics, interactionDebugGraphics } =
      drawVoidCaveChestCollisionDebug(this, chest, rect, { isOpened });

    this.chestSprites.set(chest.id, {
      ...chest,
      sprite,
      collisionBody,
      collisionDebugGraphics,
      interactionDebugGraphics,
      opened: isOpened,
    });
    if (interactionBody) this.chestBodies.set(chest.id, interactionBody);
  }

  openChest(chestId) {
    const chest = this.chestSprites.get(chestId);
    const body = this.chestBodies.get(chestId);
    if (!chest || chest.opened) return;

    chest.sprite.setTexture("vcave-chest-open");
    chest.sprite.setY(
      chest.y - (CHEST_TEXTURE_RECTS.open.height - CHEST_TEXTURE_RECTS.closed.height),
    );
    chest.sprite.setDepth(
      this.getGroundEntityDepthAt(
        chest.y + CHEST_TEXTURE_RECTS.closed.height - 4,
      ),
    );
    chest.opened = true;
    markVoidCaveStoryChestOpened(this.scene.key, chestId);
    this.currentInteractable = null;
    this.clearInteractPrompt();

    if (body) {
      this.matter.world.remove(body);
      this.chestBodies.delete(chestId);
    }
    chest.interactionDebugGraphics?.destroy();
    chest.interactionDebugGraphics = null;

    this.tweens.add({
      targets: chest.sprite,
      scaleX: { from: 0.92, to: 1 },
      scaleY: { from: 0.92, to: 1 },
      y: { from: chest.sprite.y + 4, to: chest.sprite.y },
      duration: 180,
      ease: "Back.Out",
      onComplete: () => {
        window.dispatchEvent(
          new CustomEvent(VOID_CAVE_CHEST_ANIMATION_COMPLETE_EVENT, {
            detail: { chestId },
          }),
        );
      },
    });
  }

  requestOpenChest(chestId) {
    requestVoidCaveStoryChestOpen(this, chestId);
  }

  setInteractPrompt(label) {
    window.dispatchEvent(
      new CustomEvent("interact-proximity", { detail: true }),
    );
    window.dispatchEvent(new CustomEvent("door-name", { detail: label }));
  }

  clearInteractPrompt() {
    window.dispatchEvent(
      new CustomEvent("interact-proximity", { detail: false }),
    );
    window.dispatchEvent(new CustomEvent("door-name", { detail: null }));
  }

  getGroundEntityDepthAt(y) {
    return getVoidCaveGroundEntityDepth(y, MAP_HEIGHT);
  }

  getPlayerDepthAt(x, y) {
    return isVoidCaveBridgePoint(x, y)
      ? BRIDGE_PLAYER_DEPTH
      : this.getGroundEntityDepthAt(y + 24);
  }

  update() {
    this.updatePatrolNpcs();
    this.updateNpcChatBubblePosition();
    this.syncStoryRuneStoneTeleportEffect();
    updateCaveCreatureEncounters(this);

    const sceneInputLocked =
      typeof window !== "undefined" &&
      (
        Boolean(window.__storySceneInputLocked) ||
        Boolean(window.__storyWorldEncounterPaused)
      );

    if (sceneInputLocked) {
      const playerDepth = this.getPlayerDepthAt(this.player.x, this.player.y);
      this.player.setVelocity(0, 0);
      this.player.setDepth(playerDepth);
      this.player.anims?.stop?.();
      updateLocalOrbAvatar(
        this,
        this.player,
        this.playerOrbImage,
        this.localOrbAvatarData,
        {
          x: this.player.x,
          y: this.player.y,
          vx: 0,
          depth: playerDepth,
        },
      );
      return;
    }

    const speed = 2;
    let vx = 0;
    let vy = 0;
    const { x: jx, y: jy } = this.joystick;

    if (jx || jy) {
      vx = jx * speed;
      vy = jy * speed;
    } else {
      if (this.cursors.left.isDown || this.keys.A.isDown) vx = -speed;
      else if (this.cursors.right.isDown || this.keys.D.isDown) vx = speed;
      if (this.cursors.up.isDown || this.keys.W.isDown) vy = -speed;
      else if (this.cursors.down.isDown || this.keys.S.isDown) vy = speed;
    }

    if (vx && vy) {
      if (Math.abs(vx) >= Math.abs(vy)) {
        vy = 0;
      } else {
        vx = 0;
      }
    }

    this.player.setVelocity(vx, vy);
    const playerDepth = this.getPlayerDepthAt(this.player.x, this.player.y);
    this.player.setDepth(playerDepth);

    if (
      updateLocalOrbAvatar(
        this,
        this.player,
        this.playerOrbImage,
        this.localOrbAvatarData,
        {
          x: this.player.x,
          y: this.player.y,
          vx,
          depth: playerDepth,
        },
      )
    ) {
      return;
    }

    if (Math.abs(vy) > Math.abs(vx)) {
      if (vy < 0) this.player.anims.play("walk-up", true);
      else if (vy > 0) this.player.anims.play("walk-down", true);
    } else if (vx < 0) {
      this.player.anims.play("walk-left", true);
    } else if (vx > 0) {
      this.player.anims.play("walk-right", true);
    } else {
      this.player.anims.stop();
    }
  }

  updatePatrolNpcs() {
    let nearestNpc = null;
    let nearestDistanceSq = Infinity;

    this.patrolNpcs.forEach((npc) => {
      const npcLeft = npc.sprite.x - 14;
      const npcTop = npc.sprite.y - 25;
      const npcWidth = 28;
      const npcHeight = 26;
      const playerLeft = this.player.x - 22.5;
      const playerTop = this.player.y - 32.5;
      const playerWidth = 45;
      const playerHeight = 65;
      const touchingPlayer = !(
        playerLeft + playerWidth < npcLeft
        || npcLeft + npcWidth < playerLeft
        || playerTop + playerHeight < npcTop
        || npcTop + npcHeight < playerTop
      );

      if (touchingPlayer) {
        const distanceSq =
          (this.player.x - npc.sprite.x) ** 2 + (this.player.y - npc.sprite.y) ** 2;
        if (distanceSq < nearestDistanceSq) {
          nearestDistanceSq = distanceSq;
          nearestNpc = npc;
        }
        this.setCaveNpcIdle(npc);
        return;
      }

      if (this.currentNpc === npc) {
        this.hideNpcChatBubble(npc);
      }

      if (npc.talkingUntil > this.time.now || npc.waitUntil > this.time.now) {
        this.setCaveNpcIdle(npc);
        return;
      }

      const target = npc.path[npc.pathIndex];
      if (!target) return;

      const dx = target.x - npc.sprite.x;
      const dy = target.y - npc.sprite.y;
      const distance = Math.hypot(dx, dy);

      if (distance < NPC_MOVE_SPEED + 0.2) {
        npc.sprite.setPosition(target.x, target.y);
        Phaser.Physics.Matter.Matter.Body.setPosition(npc.body, {
          x: target.x,
          y: target.y - 12,
        });
        npc.pathIndex = (npc.pathIndex + 1) % npc.path.length;
        npc.waitUntil = this.time.now + NPC_WAIT_MS;
        this.setCaveNpcIdle(npc);
        return;
      }

      const vx = (dx / distance) * NPC_MOVE_SPEED;
      const vy = (dy / distance) * NPC_MOVE_SPEED;
      npc.sprite.x += vx;
      npc.sprite.y += vy;
      npc.sprite.setDepth(this.getGroundEntityDepthAt(npc.sprite.y + 2));
      Phaser.Physics.Matter.Matter.Body.setPosition(npc.body, {
        x: npc.sprite.x,
        y: npc.sprite.y - 12,
      });

      if (Math.abs(dy) > Math.abs(dx)) {
        npc.lastDirection = vy < 0 ? "up" : "down";
        npc.sprite.anims.play(
          vy < 0 ? `${npc.key}-walk-up` : `${npc.key}-walk-down`,
          true,
        );
      } else if (vx < 0) {
        npc.lastDirection = "left";
        npc.sprite.anims.play(`${npc.key}-walk-left`, true);
      } else {
        npc.lastDirection = "right";
        npc.sprite.anims.play(`${npc.key}-walk-right`, true);
      }
    });

    this.setNpcInteractionTarget(nearestNpc);
  }
}
